<template>
  <div class="lead-history-card">
    <div class="lead-history-owner">
      <BUserTooltip
        v-if="isExistUser"
        :image-url="user.avatarUrl"
        :name="user.name"
        :user-id="user.id"
        :size="40"
      />
      <div
        v-else-if="isDisplaySystemUser"
        class="system-user"
      >
        <img
          class="system-user-img"
          :src="iconSrc"
        >
      </div>
      <div
        v-else-if="isEvent"
        class="event-reaction"
      >
        <img
          class="event-reaction-img"
          :src="iconSrc"
        >
      </div>
      <div
        v-else-if="isBoxilEvent"
        class="boxil-event"
      >
        <img
          class="boxil-event-img"
          :src="boxilIconSrc"
        >
      </div>
    </div>
    <div class="lead-history-card-content">
      <BExpandableCard
        :expandable="toggle"
        :class="classes"
        :default-expanded="expand"
        show-expand-more
      >
        <template #info>
          <div
            class="lead-history-card-icon"
            :class="iconClass"
          >
            <template v-if="!isEvent || !isBoxilEvent">
              <BTooltip
                top
                :content="iconTooltip"
                :disabled="!iconTooltip"
              >
                <Component
                  :is="iconComponent"
                  class="icon"
                  :size="iconSize"
                  :icon-class="customIconClass"
                  :icon="icon"
                >
                  {{ icon }}
                </Component>
              </BTooltip>
            </template>
            <slot
              v-else
              name="icon"
            />
          </div>
          <div class="lead-history-card-info">
            <div class="info-text">
              {{ text }}
              <BTooltip
                top
                :content="status.name"
              >
                <BIcon
                  v-if="hasStatus"
                  class="status-icon"
                  size="mini"
                  :type="status.type"
                >
                  {{ status.icon }}
                </BIcon>
              </BTooltip>
            </div>
            <div class="info-date">
              <slot name="date" />
            </div>
            <div
              v-if="callTarget != null"
              class="info-call-target"
            >
              <BDivider />
              <div class="info-call-target-content">
                <LeadLink :call-target-id="callTarget.id">
                  {{ callTarget.fullName || `ID: ${callTarget.id}` }}
                </LeadLink>
                <BBtn
                  size="mini"
                  fab
                  flat
                  class="pick-up-call-target"
                  :class="{'pick-up': isPickUpCallTarget}"
                  @click.stop="handlePickUpCallTarget"
                >
                  <BIcon
                    size="small"
                    :outlined="!isPickUpCallTarget"
                  >
                    contact_page
                  </BIcon>
                </BBtn>
              </div>
            </div>
          </div>
          <div
            v-if="$slots.additionalInfo"
            class="lead-history-card-additional-info"
          >
            <slot name="additionalInfo" />
          </div>
        </template>
        <template #actions>
          <div class="lead-history-card-sub">
            <slot name="action" />
            <BDivider
              v-if="callTarget == null"
              direction="vertical"
            />
            <CopyUrl
              :call-target-id="callTargetId"
              :uuid="history.uuid"
            />
          </div>
        </template>
        <div
          class="lead-history-card-body"
        >
          <slot />
        </div>
      </BExpandableCard>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import type { CallTargetMinimum, HistoryCommon } from '@/api/openapi';
import LeadLink from '../../general/LeadLink.vue';
import CopyUrl from './CopyUrl.vue';

type TProps = {
  history: HistoryCommon;
  callTargetId: number;
  iconComponent?: string;
  customIconClass?: string;
  iconSize?: string;
  text?: string;
  icon?: string;
  iconTooltip?: string;
  iconType?: string;
  user?: Record<string, unknown>;
  toggle?: boolean;
  expand?: boolean;
  isEvent?: boolean;
  isBoxilEvent?: boolean;
  status?: Record<string, unknown>;
  eventEmotion?: string | null;
  isDisplaySystemUser?: boolean;
  callTarget?: CallTargetMinimum;
}

const props = withDefaults(defineProps<TProps>(), {
  iconComponent: 'bIcon',
  customIconClass: '',
  iconSize: 'medium',
  text: undefined,
  icon: undefined,
  iconTooltip: undefined,
  iconType: 'default',
  user: () => ({}),
  toggle: true,
  expand: true,
  isEvent: false,
  isBoxilEvent: false,
  status: () => ({}),
  eventEmotion: null,
  isDisplaySystemUser: false,
  callTarget: undefined,
});
defineOptions({
  inheritAttrs: false,
});

// Computed properties
const classes = computed(() => ({
  'is-toggle': props.toggle,
}));

const iconClass = computed(() => `icon-${props.iconType}`);

const hasStatus = computed(() => Object.keys(props.status).length > 0);

const isExistUser = computed(() => !!props.user && Object.keys(props.user).length > 0);

const iconSrc = computed(() => {
  if (props.eventEmotion === 'positive') return new URL('@/assets/positive_reaction.svg', import.meta.url).href;
  if (props.eventEmotion === 'negative') return new URL('@/assets/negative_reaction.svg', import.meta.url).href;
  return new URL('@/assets/positive_reaction.svg', import.meta.url).href;
});

const boxilIconSrc = computed(() => new URL('@/assets/boxil-saas-logo.svg', import.meta.url).href);

const pickUpCallTargetId = defineModel<number>('pickUpCallTargetId');
const isPickUpCallTarget = computed(() => pickUpCallTargetId.value === props.callTarget?.id);
const handlePickUpCallTarget = () => {
  if (pickUpCallTargetId.value === props.callTarget?.id) {
    pickUpCallTargetId.value = undefined;
  } else {
    pickUpCallTargetId.value = props.callTarget?.id;
  }
};
</script>

<style lang="scss" scoped>
$lead-history-owner-width: 42.5px;
$lead-history-owner-margin-right: 10px;

.lead-history-card {
  display: flex;
  margin-bottom: 20px;

  &-content {
    width: calc(100% - ($lead-history-owner-width + $lead-history-owner-margin-right));

    :deep(.b-divider) {
      margin: $basespace-400 0;
      background-color: $bdcolor-light;
    }
  }

  &-sub {
    display: flex;
    align-items: center;

    .b-divider.is-vertical {
      padding: $basespace-100 0;
      margin: 0 $basespace-200;
      background-color: $bdcolor-light;
    }
  }

  &-icon {
    align-self: center;
    $size: 36px;
    @include m-flex-center;
    @include m-fixed-width($size);
    @include m-fixed-height($size);
    border-radius: 4px;
    margin-right: $basespace-300;
    margin-left: $basespace-50;

    .icon {
      color: $textcolor-white;
    }

    &.icon-default {
      background-color: $bgcolor-base;

      .icon {
        color: $textcolor-base;
      }
    }

    &.icon-hot {
      background-color: $basecolor-hot;
    }

    &.icon-cold {
      background-color: $basecolor-cold;
    }

    &.icon-success {
      background-color: $basecolor-success;
    }

    &.icon-emphasis {
      background: $bgcolor-aurora;
    }

    &.icon-boxil {
      background-color: #30B0B0;
    }
  }

  &-info {
    align-self: center;
    flex-grow: 1;
    padding: 0 $basespace-300;

    .info-text {
      margin-right: 12px;
      font-weight: 600;
    }
    .info-date {
      color: $textcolor-light;
      padding-top: $basespace-50 + 2px;
    }
    .info-date, .info-call-target {
      font-size: $fontsize-100;
    }
    .b-divider {
      margin: $basespace-100 0;
    }
    .info-call-target {
      .info-call-target-content {
        @include m-flex-align-center;
      }
      a {
        align-self: center;
      }
      .pick-up-call-target {
        margin-left: $basespace-100;
        &.pick-up .b-icon {
          color: $basecolor-success;
        }
        .b-icon {
          margin-left: 2px; /* アイコンがやや左寄りなのでボタン中央に来るよう調整 */
        }
      }
    }
  }    
  &-additional-info {
    @include m-flex-align-center;
    margin: 0 $basespace-200;
  }
}

.lead-history-owner {
  margin-top: 11px;
  margin-right: 10px;
  width: 42.5px;
  height: 42.5px;
}

.image {
  &-container-base {
    position: relative;
    width: 40px;
    height: 40px;
  }

  &-large {
    position: absolute;
    width: 60px;
    height: 60px;
    top: 0;
    margin-left: -10px;
    padding: 0;
  }

  &-small {
    position: absolute;
    width: 40px;
    height: 40px;
    top: 0;
    padding: 0;
  }
}

.system-user {
  @extend .image-container-base;
  
  &-img {
    @extend .image-large;
  }
}

.event-reaction {
  @extend .image-container-base;
  
  &-img {
    @extend .image-large;
  }
}

.boxil-event {
  @extend .image-container-base;
  
  &-img {
    @extend .image-small;
  }
}

.status-icon {
  margin-left: 5px;
  margin-bottom: 3px;
}
</style>
